import React, { useEffect, useState, useCallback } from "react";
import Router from "next/router";
import classNames from "classnames";
import Link from "next/link";
import { CSSTransition } from "react-transition-group";

import { User } from "lib/api";
import Avatar from "components/Avatar";
import BlueTag from "components/BlueTag";
import useSession from "lib/useSession";
import LogoLight from "assets/logo-full.svg";
import LogoDark from "assets/logo-full-dark.svg";
import ChevronDownIcon from "assets/icons/chevron-down.svg";

import styles from "./Navbar.module.css";

const SignedIn = ({ currentUser }: { currentUser: User }) => {
  return (
    <>
      <div className={styles.navUserInfo}>
        <Link href={`/${currentUser.handle}`}>
          <a>
            <Avatar {...currentUser} />
          </a>
        </Link>
        <ChevronDownIcon className={classNames(styles.userInfoHoverTarget, "icon")} />
        <div className={styles.userDropdown}>
          <Link href={`/${currentUser.handle}`}>
            <a className={styles.dropdownItem}>View Account</a>
          </Link>
          <Link href={`/users/sign_out`}>
            <a className={classNames(styles.dropdownItem, styles.danger)}>Log Out</a>
          </Link>
        </div>
      </div>
      <Link href={`/users/sign_out`}>
        <a className={classNames(styles.navlink, styles.mobileLogOut)}>Log Out</a>
      </Link>
    </>
  );
};

const SignedOut = () => {
  return (
    <>
      <Link href="/users/sign_in">
        <a className={styles.navlink}>Log in</a>
      </Link>
      <Link href="/download">
        <a className={styles.downloadLink}>Download</a>
      </Link>
    </>
  );
};

const UserInfo = () => {
  const session = useSession();
  const nodeRef = React.useRef(null);

  let innerComponent = <></>;

  if (session && session.user) {
    innerComponent = <SignedIn currentUser={session.user} />;
  } else if (session && !session.user) {
    innerComponent = <SignedOut />;
  }

  return (
    <CSSTransition
      in={!!session}
      nodeRef={nodeRef}
      timeout={300}
      classNames={{
        enter: styles.userinfoTransitionEnter,
        enterActive: styles.userinfoTransitionEnterActive,
        exit: styles.userinfoTransitionExit,
        exitActive: styles.userinfoTransitionExitActive,
      }}
    >
      <div ref={nodeRef} className={styles.userInfo}>
        {innerComponent}
      </div>
    </CSSTransition>
  );
};

export default function Navbar() {
  const [menuOpened, setMenuOpened] = useState<boolean>(false);

  const hideMenu = useCallback(() => {
    setMenuOpened(false);
  }, [setMenuOpened]);

  useEffect(() => {
    Router.events.on("routeChangeStart", hideMenu);

    return () => {
      Router.events.off("routeChangeStart", hideMenu);
    };
  }, [hideMenu]);

  /* eslint-disable @next/next/no-html-link-for-pages */
  return (
    <>
      <div className={styles.controlsWrapper}>
        <Link href="/">
          <a className={styles.logo}>
            <LogoDark className="only-light-theme" />
            <LogoLight className="only-dark-theme" />
          </a>
        </Link>
        <div
          className={classNames(styles.navToggle, { [styles.active]: menuOpened })}
          onClick={() => setMenuOpened(!menuOpened)}
        >
          <div className={styles.navToggleLine} />
          <div className={styles.navToggleLine} />
          <div className={styles.navToggleLine} />
        </div>
      </div>
      <header className={classNames(styles.navbar, { [styles.opened]: menuOpened })}>
        <div className={styles.menu}>
          <a className={styles.navlink} href="/store">
            Store
          </a>
          <Link href="/developers">
            <a className={styles.navlink}>Developer</a>
          </Link>
          <div>
            <Link href="/teams">
              <a className={styles.navlink}>Teams</a>
            </Link>
            <BlueTag>New</BlueTag>
          </div>
          <Link href="/pricing">
            <a className={styles.navlink}>Pricing</a>
          </Link>

          <a className={styles.navlink} href="/blog">
            Blog
          </a>
        </div>
        <UserInfo />
      </header>
    </>
  );
  /* eslint-enable @next/next/no-html-link-for-pages */
}
